import { useState } from "react";
import { Globe, ThumbsUp, MessageSquare, Repeat2, Send } from "lucide-react";
import { imageUrl } from "@agent/lib/api";

const LIMIT = 3000;
const TRUNCATE_AT = 210;

export function CharCounter({ content }) {
  const len = content?.length || 0;
  const over = len > LIMIT;
  const warn = len > LIMIT * 0.9;
  return (
    <span data-testid="char-counter" className={`text-xs font-semibold px-3 py-1 rounded-full border bg-white shadow-sm ${over ? "text-rose-600 border-rose-200" : warn ? "text-amber-600 border-amber-200" : "text-slate-500 border-slate-200"}`}>
      {len}/{LIMIT} {over && "· over limit"}
    </span>
  );
}

export function PostPreview({ content, imageIds, pageName = "Your Company Page" }) {
  const [expanded, setExpanded] = useState(false);
  const images = (imageIds || []).filter(Boolean);
  const text = content || "";
  const truncated = !expanded && text.length > TRUNCATE_AT;
  const shown = truncated ? text.slice(0, TRUNCATE_AT) : text;

  return (
    <div data-testid="linkedin-preview" className="bg-white text-[#1d1d1f] rounded-lg overflow-hidden max-w-[555px] w-full shadow-2xl" style={{ fontFamily: "-apple-system, system-ui, sans-serif" }}>
      <div className="p-4 pb-2 flex items-start gap-3">
        <div className="w-12 h-12 bg-[#0a66c2] flex items-center justify-center text-white font-bold text-lg rounded-sm shrink-0">
          {pageName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm leading-tight truncate">{pageName}</p>
          <p className="text-xs text-[#666]">12,480 followers</p>
          <p className="text-xs text-[#666] flex items-center gap-1">Just now · <Globe size={11} /></p>
        </div>
      </div>
      <div className="px-4 pb-3 text-sm whitespace-pre-wrap break-words leading-snug">
        {text ? (
          <>
            {shown}
            {truncated && (
              <button data-testid="preview-see-more" onClick={() => setExpanded(true)} className="text-[#666] hover:text-[#0a66c2] font-medium ml-1">…see more</button>
            )}
            {expanded && text.length > TRUNCATE_AT && (
              <button data-testid="preview-see-less" onClick={() => setExpanded(false)} className="block text-[#666] hover:text-[#0a66c2] font-medium mt-1">see less</button>
            )}
          </>
        ) : (
          <span className="text-[#999] italic">Your post preview will appear here…</span>
        )}
      </div>
      {images.length === 1 && (
        <img data-testid="preview-image" src={imageUrl(images[0])} alt="post visual" className="w-full max-h-[420px] object-cover" />
      )}
      {images.length > 1 && (
        <div data-testid="preview-image-grid" className={`grid gap-0.5 ${images.length === 2 ? "grid-cols-2" : "grid-cols-2 grid-rows-2"}`}>
          {images.slice(0, 4).map((id, i) => (
            <div key={id} className={`relative ${images.length === 3 && i === 0 ? "row-span-2" : ""}`}>
              <img src={imageUrl(id)} alt="post visual" className="w-full h-full object-cover" style={{ minHeight: 130, maxHeight: 260 }} />
              {i === 3 && images.length > 4 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-lg">+{images.length - 4}</div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-[#e8e8e8] px-2 py-1 flex justify-around">
        {[{ icon: ThumbsUp, l: "Like" }, { icon: MessageSquare, l: "Comment" }, { icon: Repeat2, l: "Repost" }, { icon: Send, l: "Send" }].map(({ icon: Icon, l }) => (
          <span key={l} className="flex items-center gap-1.5 text-[#666] text-xs font-medium py-2 px-3">
            <Icon size={15} /> {l}
          </span>
        ))}
      </div>
    </div>
  );
}
